// CARO. Casa de estilo — per-page <title>/description text, shared by the
// Worker (worker/lib/pageMeta.ts, the <head> crawlers see) and the client
// (useMeta in each page, the <head> after client-side navigation), so both
// sides always say the same thing (tests/meta-parity*.test.tsx).
//
// Pure data + functions (shared layer: no DOM/Worker globals). Self-contained on
// purpose: seo/index.ts re-exports it, so it must not import from there.

import type { Locale, Product, PublicPack } from '@/types';
import type { EDIT_CATEGORIES } from '@/content/articles';
import { isFind } from '@/lib/catalog/finds';
import { packDescription, packName } from '@/lib/catalog/packs';
import { localizeProduct } from '@/lib/i18n-content';
import { formatMoney } from '@/lib/money';

/** Site-wide default meta description per locale. */
export const SITE_DESCRIPTION: Record<Locale, string> = {
  es: 'Una casa de estilo, belleza y bienestar para la mujer que evoluciona. Selección personal de CARO.',
  en: 'A house of style, beauty and wellbeing for the woman who evolves. Personally selected by CARO.',
  fr: 'Une maison de style, de beauté et de bien-être pour la femme qui évolue. Une sélection personnelle de CARO.',
};

/** A page's short title (buildTitle adds the brand suffix) and meta description. */
export interface PageText {
  /** Undefined = the home (brand + tagline). */
  title?: string;
  description: string;
}

/** Static, indexable pages with per-locale title/description. */
export const STATIC_PAGES: Record<string, Record<Locale, PageText>> = {
  '/': {
    es: { description: SITE_DESCRIPTION.es },
    en: { description: SITE_DESCRIPTION.en },
    fr: { description: SITE_DESCRIPTION.fr },
  },
  '/shop': {
    es: { title: 'Tienda', description: 'La selección de CARO: estilo, belleza y objetos de diseño elegidos con criterio.' },
    en: { title: 'Shop', description: "CARO's selection: style, beauty and design objects chosen with judgement." },
    fr: { title: 'Shop', description: 'La sélection de CARO : style, beauté et objets de design choisis avec discernement.' },
  },
  '/shop/beauty': {
    es: { title: 'Belleza', description: 'Belleza seleccionada por CARO para la piel que evoluciona.' },
    en: { title: 'Beauty', description: 'Beauty selected by CARO for skin that evolves.' },
    fr: { title: 'Beauté', description: 'La beauté sélectionnée par CARO pour une peau qui évolue.' },
  },
  '/shop/estilo': {
    es: { title: 'Estilo', description: 'Moda y accesorios seleccionados por CARO: lo que merece quedarse.' },
    en: { title: 'Style', description: 'Fashion and accessories selected by CARO: what deserves to stay.' },
    fr: { title: 'Style', description: 'Mode et accessoires sélectionnés par CARO : ce qui mérite de rester.' },
  },
  // Hallazgos: Estilo's one-off pieces (lib/catalog/finds).
  '/shop/hallazgos': {
    es: { title: 'Hallazgos', description: 'Hallazgos de CARO: piezas únicas de moda y accesorios que Carolina encuentra y elige. Hay una de cada.' },
    en: { title: 'Finds', description: "CARO's finds: one-off fashion and accessories that Carolina discovers and chooses. One of each." },
    fr: { title: 'Trouvailles', description: 'Les trouvailles de CARO : des pièces uniques de mode et d’accessoires que Carolina déniche et choisit. Il y en a une de chaque.' },
  },
  '/shop/casa': {
    es: { title: 'Casa', description: 'Arte y objetos de diseño de coleccionista, elegidos con criterio.' },
    en: { title: 'Casa', description: 'Collectible art and design objects, chosen with judgement.' },
    fr: { title: 'Casa', description: 'Art et objets de design de collection, choisis avec discernement.' },
  },
  '/the-edit': {
    es: { title: 'Edit', description: 'La editorial de CARO: estilo, belleza, bienestar y casa.' },
    en: { title: 'Edit', description: "CARO's editorial: style, beauty, wellbeing and home." },
    fr: { title: 'Edit', description: 'Les pages éditoriales de CARO : style, beauté, bien-être et maison.' },
  },
  '/caro': {
    es: { title: 'Sobre CARO', description: 'Carolina Collaer, fundadora y curadora de CARO. Casa de estilo.' },
    en: { title: 'About CARO', description: 'Carolina Collaer, founder and curator of CARO. Casa de estilo.' },
    fr: { title: 'À propos de CARO', description: 'Carolina Collaer, fondatrice et curatrice de CARO. Casa de estilo.' },
  },
  '/caro/servicios': {
    es: {
      title: 'Asesora de imagen en Sitges y el Penedès',
      description: 'Asesoría de imagen, personal shopper, colorimetría y maquillaje con Carolina Collaer. Presencial en Sitges, Garraf y Penedès, y online.',
    },
    en: {
      title: 'Image consultant in Sitges and Penedès',
      description: 'Image consulting, personal shopping, colour analysis and make-up with Carolina Collaer. In person in Sitges, Garraf and Penedès, and online.',
    },
    fr: {
      title: 'Conseillère en image à Sitges et Penedès',
      description: 'Conseil en image, personal shopper, colorimétrie et maquillage avec Carolina Collaer. En personne à Sitges, dans le Garraf et le Penedès, et en ligne.',
    },
  },
  '/caro/filosofia': {
    es: { title: 'Filosofía', description: 'La mirada de CARO sobre el estilo con intención.' },
    en: { title: 'Philosophy', description: "CARO's view on style with intention." },
    fr: { title: 'Philosophie', description: 'Le regard de CARO sur un style choisi avec intention.' },
  },
  '/contacto': {
    es: { title: 'Contacto', description: 'Escríbenos. CARO. Casa de estilo.' },
    en: { title: 'Contact', description: 'Get in touch. CARO. Casa de estilo.' },
    fr: { title: 'Contact', description: 'Écrivez-nous. CARO. Casa de estilo.' },
  },
  '/newsletter': {
    es: { title: 'La carta de CARO', description: 'Una carta de CARO: selecciones, notas de estilo, belleza y bienestar para la mujer que evoluciona. Sin ruido.' },
    en: { title: 'A letter from CARO', description: 'A letter from CARO: selections, style notes, beauty and wellbeing for the woman who evolves. No noise.' },
    fr: { title: 'La lettre de CARO', description: 'La lettre de CARO : sélections, notes de style, beauté et bien-être pour la femme qui évolue. Sans bruit.' },
  },
};

/** Paths of the static indexable pages (the sitemap must list every one). */
export const STATIC_PAGE_PATHS: readonly string[] = Object.keys(STATIC_PAGES);

/** Title + description of a static page (a key of STATIC_PAGES) for `locale`. */
export function staticPageText(path: string, locale: Locale): PageText {
  const page = STATIC_PAGES[path];
  if (!page) throw new Error(`No static page text for ${path}`);
  return page[locale];
}

// --- The Edit categories ------------------------------------------------------

export type EditCategorySlug = (typeof EDIT_CATEGORIES)[number]['slug'];

/** Edit category labels per locale (mirrors the dictionary's `edit.cat.*`). */
export const EDIT_CATEGORY_LABELS: Record<EditCategorySlug, Record<Locale, string>> = {
  estilo: { es: 'Estilo', en: 'Style', fr: 'Style' },
  belleza: { es: 'Belleza', en: 'Beauty', fr: 'Beauté' },
  bienestar: { es: 'Bienestar', en: 'Wellbeing', fr: 'Bien-être' },
  casa: { es: 'Casa', en: 'Casa', fr: 'Casa' },
  'mujer-40': { es: 'Mujer 40+', en: 'Women 40+', fr: 'Femme 40+' },
};

/** Max length of a generated meta description (Google shows ~155 characters). */
export const META_DESCRIPTION_MAX = 155;

/**
 * Meta description of an Edit category, derived from its intro (the
 * dictionary's `edit.catIntro.<slug>`, Markdown): plain text, whitespace
 * collapsed, then as many WHOLE leading sentences as fit in
 * META_DESCRIPTION_MAX; a first sentence that is already longer is cut at a
 * word boundary with «…». Pure: the Worker and the client pass the same intro.
 */
export function editCategoryDescription(intro: string): string {
  const text = collapseWhitespace(intro.replace(/^#+ */gm, '').replace(/\*\*/g, ''));
  // Sentence ends: . ! ? … followed by a breaking space. Not `\s`, which also
  // matches the no-break space French puts before ? and !.
  const sentences = text.split(/(?<=[.!?\u2026])[^\S\u00a0\u202f]+/);
  let out = '';
  for (const sentence of sentences) {
    const next = out ? `${out} ${sentence}` : sentence;
    if (next.length > META_DESCRIPTION_MAX) break;
    out = next;
  }
  return out || cutAtWord(text, META_DESCRIPTION_MAX);
}

/** Localized label of an Edit category slug (undefined for an unknown slug). */
export function editCategoryLabel(slug: string, locale: Locale): string | undefined {
  return (EDIT_CATEGORY_LABELS as Record<string, Record<Locale, string>>)[slug]?.[locale];
}

/**
 * An Edit category page (`/the-edit/:category`): `<label> · The Edit` and a
 * description derived from `intro`, the category's `edit.catIntro.<slug>` text
 * in `locale` (both sides read it from the dictionary: `translate` / `t`).
 */
export function editCategoryPageText(slug: EditCategorySlug, locale: Locale, intro: string): Required<PageText> {
  const label = EDIT_CATEGORY_LABELS[slug][locale];
  return { title: `${label} · The Edit`, description: editCategoryDescription(intro) || SITE_DESCRIPTION[locale] };
}

// --- Product pages ------------------------------------------------------------

/**
 * Collapse runs of whitespace (double spaces, newlines) into one space and trim.
 * No-break spaces (U+00A0, U+202F) are kept: French and «10 %» typography need them.
 */
function collapseWhitespace(text: string): string {
  return text.replace(/[^\S\u00a0\u202f]+/g, ' ').trim();
}

/** Max length of a product meta description (Google shows ~155 characters). */
export const PRODUCT_DESCRIPTION_MAX = META_DESCRIPTION_MAX;

/**
 * Cut `text` at a word boundary to at most `max` characters, ending in «…»
 * (no dangling punctuation before it). Unchanged when it already fits.
 */
function cutAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const room = text.slice(0, max); // keep 1 char for «…»
  const lastSpace = room.lastIndexOf(' ');
  const cut = lastSpace > 0 ? room.slice(0, lastSpace) : room.slice(0, max - 1);
  // No dangling punctuation before the ellipsis («piel,…» → «piel…»).
  return `${cut.replace(/[\s.,;:·\-–(]+$/, '')}…`;
}

/**
 * Short `<title>` of a product page (pass the product already localized):
 * `metadata.seoTitle` when set, else `name · brand` (the brand is not repeated
 * when the name already contains it), with whitespace collapsed. buildTitle()
 * adds the « · CARO. Casa de estilo» suffix. The h1 and og:title stay the name.
 */
export function productSeoTitle(product: Pick<Product, 'name' | 'brand' | 'metadata'>): string {
  const seo = product.metadata?.seoTitle;
  if (typeof seo === 'string' && seo.trim()) return collapseWhitespace(seo);
  const name = collapseWhitespace(product.name);
  const brand = collapseWhitespace(product.brand ?? '');
  return brand && !name.includes(brand) ? `${name} · ${brand}` : name;
}

/**
 * Meta description of a product page (pass the product already localized):
 * `subtitle. description`, whitespace collapsed, cut at a word boundary to at
 * most PRODUCT_DESCRIPTION_MAX characters (ending in «…» when cut). Empty
 * string when the product has neither.
 */
export function productSeoDescription(product: Pick<Product, 'subtitle' | 'description'>): string {
  const subtitle = collapseWhitespace(product.subtitle ?? '');
  const description = collapseWhitespace(product.description ?? '');
  // Join with «. » unless the subtitle already ends a sentence (no «..»).
  const text = subtitle && description
    ? `${subtitle}${/[.!?…:;]$/.test(subtitle) ? '' : '.'} ${description}`
    : subtitle || description;
  return cutAtWord(text, PRODUCT_DESCRIPTION_MAX);
}

/**
 * A product page (pass the product already localized): the SEO title
 * (productSeoTitle), the description (productSeoDescription, else the site
 * description; a find ignores its subtitle, which its page does not show) and the og:title, which stays the product name.
 */
export function productPageText(
  product: Pick<Product, 'name' | 'brand' | 'category' | 'subtitle' | 'description' | 'metadata'>,
  locale: Locale,
): Required<PageText> & { ogTitle: string } {
  return {
    title: productSeoTitle(product),
    description:
      productSeoDescription(isFind(product) ? { ...product, subtitle: null } : product) || SITE_DESCRIPTION[locale],
    ogTitle: collapseWhitespace(product.name),
  };
}

/**
 * Draft and archived pieces still answer (old links keep working) but are not
 * indexed: `noindex,follow` and no prerendered body on the Worker, `noindex` in useMeta.
 */
export function isIndexableProduct(product: Pick<Product, 'status'>): boolean {
  return product.status !== 'draft' && product.status !== 'archived';
}

// --- Pack pages (docs/plan-packs.md) -------------------------------------------

/**
 * Max length of a pack page's short title (name + component names), before
 * buildTitle's « · CARO. Casa de estilo» suffix, which is not counted (as for
 * product titles).
 */
export const PACK_TITLE_MAX = 60;

const LIST_LOCALE: Record<Locale, string> = { es: 'es-ES', en: 'en-GB', fr: 'fr-FR' };
const MORE: Record<Locale, string> = { es: 'y más', en: 'and more', fr: 'et plus' };
const PACK_SIGNOFF: Record<Locale, string> = {
  es: 'Una selección personal de CARO.',
  en: 'Personally selected by CARO.',
  fr: 'Une sélection personnelle de CARO.',
};

/** «A, B y C» / «A, B and C» / «A, B et C». */
function joinList(items: string[], locale: Locale): string {
  try {
    return new Intl.ListFormat(LIST_LOCALE[locale], { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/** What packPageText reads from a pack (a PublicPack fits). */
export type PackPageInput = Pick<
  PublicPack,
  | 'name'
  | 'i18n'
  | 'description'
  | 'discountPercent'
  | 'primaryCategory'
  | 'seq'
  | 'items'
  | 'components'
  | 'priceCents'
  | 'compareAtCents'
>;

/**
 * A pack page (`/shop/pack/<slug>`), shared by the client (useMeta) and the
 * Worker (pageMeta, Phase 5):
 * - title: the pack name (packName, per locale), then « · » and the component
 *   names (localized) while they fit PACK_TITLE_MAX («… y más» when not all
 *   do; the name alone when not even one fits); buildTitle adds the suffix;
 * - description: «<name>: juntos por 86,99 € en lugar de 96,70 €, ahorras
 *   9,71 €. 3× A, 1× B y 1× C.» + the pack description (else a sign-off when
 *   short), cut at a word to META_DESCRIPTION_MAX;
 * - ogTitle: the pack name.
 */
export function packPageText(pack: PackPageInput, locale: Locale): Required<PageText> & { ogTitle: string } {
  const name = collapseWhitespace(packName(pack, locale));
  const byId = new Map(pack.components.map((c) => [c.id, localizeProduct(c, locale)]));
  const lines = pack.items.flatMap((it) => {
    const c = byId.get(it.productId);
    return c ? [{ name: collapseWhitespace(c.name), quantity: it.quantity }] : [];
  });

  // Title: as many component names as fit.
  let title = name;
  const names = lines.map((l) => l.name);
  for (let n = names.length; n >= 1; n--) {
    const list = n === names.length ? joinList(names, locale) : `${names.slice(0, n).join(', ')} ${MORE[locale]}`;
    const candidate = `${name} · ${list}`;
    if (candidate.length <= PACK_TITLE_MAX) {
      title = candidate;
      break;
    }
  }

  const colon = locale === 'fr' ? ' :' : ':';
  const list = joinList(lines.map((l) => `${l.quantity}× ${l.name}`), locale);
  const price = formatMoney(pack.priceCents);
  const before = formatMoney(pack.compareAtCents);
  const saving = formatMoney(pack.compareAtCents - pack.priceCents);
  const hasSaving = pack.compareAtCents > pack.priceCents;
  const deal = {
    es: hasSaving ? `juntos por ${price} en lugar de ${before}, ahorras ${saving}.` : `juntos por ${price}.`,
    en: hasSaving ? `together for ${price} instead of ${before}, you save ${saving}.` : `together for ${price}.`,
    fr: hasSaving
      ? `ensemble à ${price} au lieu de ${before}, vous économisez ${saving}.`
      : `ensemble à ${price}.`,
  }[locale];
  // The deal first, so a long component list is what gets cut, never the price.
  let text = `${name}${colon} ${deal} ${list}.`;
  const description = collapseWhitespace(packDescription(pack, locale));
  if (description) text += ` ${description}`;
  if (text.length < 120) text += ` ${PACK_SIGNOFF[locale]}`;
  return {
    title,
    description: cutAtWord(collapseWhitespace(text), META_DESCRIPTION_MAX),
    ogTitle: name,
  };
}
